import type { IconDefinition } from "@fortawesome/fontawesome-common-types"
import { faCartShopping } from "@fortawesome/pro-regular-svg-icons"
import {
  Button,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from "@mui/material"
import theme, { FaIcon } from "@skistarab/mui-theme"
import { useTranslation } from "react-i18next"

import StyledHelperText from "../StyledHelperText"

type PriceRowProps = {
  label: string
  price: string
}

const PriceRow = ({ label, price }: PriceRowProps) => (
  <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
    <Typography
      variant="body1"
      component="span"
      sx={{ paddingTop: { md: "3px" } }}
    >
      {label}:
    </Typography>
    <Typography
      component="span"
      variant="h6"
      sx={{
        [theme.breakpoints.up("md")]: {
          ...theme.typography.h5,
        },
      }}
    >
      {price}
    </Typography>
  </Stack>
)

type CheckoutSectionProps = {
  orderTotalPriceText: string
  isLoading: boolean
  error: string | null
  onCheckout: () => void
  monthlyPriceText?: string
  buttonLabel?: string
  loadingLabel?: string
  buttonIcon?: IconDefinition
  secondaryButtonLabel?: string
  onSecondaryClick?: () => void
}

const CheckoutSection = ({
  orderTotalPriceText,
  isLoading,
  error,
  onCheckout,
  monthlyPriceText,
  buttonLabel,
  loadingLabel,
  buttonIcon,
  secondaryButtonLabel,
  onSecondaryClick,
}: CheckoutSectionProps) => {
  const { t } = useTranslation()

  const label = buttonLabel ?? t("passHolderDetails.addToCart")
  const loading = loadingLabel ?? t("passHolderDetails.addingToCart")
  const icon = buttonIcon ?? faCartShopping

  return (
    <>
      <Divider />
      <Stack alignItems="flex-end" spacing={1}>
        {monthlyPriceText && (
          <PriceRow
            label={t("passHolderDetails.pricePerMonth")}
            price={monthlyPriceText}
          />
        )}
        <PriceRow
          label={
            monthlyPriceText
              ? t("passHolderDetails.firstPayment")
              : t("passHolderDetails.amountToPay")
          }
          price={orderTotalPriceText}
        />

        {error && <StyledHelperText helperText={error} severity="error" />}
        <Button
          variant="contained"
          fullWidth
          disabled={isLoading}
          onClick={onCheckout}
          startIcon={
            isLoading ? <CircularProgress size={20} /> : <FaIcon icon={icon} />
          }
          sx={secondaryButtonLabel ? undefined : { maxWidth: { md: 300 } }}
        >
          {isLoading ? loading : label}
        </Button>
        {secondaryButtonLabel && (
          <Button
            variant="outlined"
            color="secondary"
            fullWidth
            onClick={onSecondaryClick}
          >
            {secondaryButtonLabel}
          </Button>
        )}
      </Stack>
    </>
  )
}

export default CheckoutSection
